import {
  Combobox,
  ComboboxExtrasButton,
  ComboboxOption,
} from '@squareup/dex-ui';
import React, { FC, memo, useCallback, useMemo } from 'react';

import { ApiExplorerFieldSpecialValuesDropdown } from '../helpers/ApiExplorerFieldSpecialValuesDropdown';
import { usePrimitiveProps } from '../hooks/nullable-hooks';
import { ApiExplorerPrimitiveFieldProps } from '../props';

interface Props extends ApiExplorerPrimitiveFieldProps {
  dropdownValues?: Array<ComboboxOption> | undefined;
  isEmptyStringAllowed?: boolean | undefined;
}

const ApiExplorerStringField: FC<Props> = memo(
  ({
    currentValue,
    onChange,
    path,
    name,
    type,
    actionButton,
    dropdownValues,
    isNullable,
    isEmptyStringAllowed = true,
  }) => {
    const onInputChangeCallback = useCallback(
      (value: string) => {
        if (value === '') {
          onChange(undefined, path, 'remove');
        } else {
          if (type === 'integer' || type === 'number') {
            const parsedValue = Number.parseInt(value, 10);
            if (!Number.isNaN(parsedValue)) {
              onChange(parsedValue, path);
            } else {
              onChange(value, path);
            }
          } else {
            onChange(value, path);
          }
        }
      },
      [onChange, path, type]
    );

    const inputType = useMemo(
      () => (type === 'integer' ? 'number' : type),
      [type]
    );

    const { placeholder, icon } = usePrimitiveProps(
      name,
      currentValue,
      isEmptyStringAllowed
    );

    return (
      <Combobox
        name={name}
        placeholder={placeholder}
        leadingAccessory={icon}
        value={currentValue?.toString()}
        onChange={onInputChangeCallback}
        type={inputType}
        actionButton={actionButton}
        comboboxValues={dropdownValues}
        extrasButton={
          <ApiExplorerFieldSpecialValuesDropdown
            currentValue={currentValue}
            showNullable={isNullable}
            showEmptyString={type === 'string' ? isEmptyStringAllowed : false}
            path={path}
            onChange={onChange}
            trigger={<ComboboxExtrasButton />}
          />
        }
      />
    );
  }
);

ApiExplorerStringField.displayName = 'ApiExplorerStringField';

export { ApiExplorerStringField };
